import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { PostHogProvider } from '@posthog/react';
import type { PostHogConfig } from 'posthog-js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  AuthProvider,
  flowOverrideService,
  refUrlService,
} from '@/features/auth';
import { SignalRProvider } from '@/providers/SignalR';
import { OfferEventsSubscriber } from '@/features/offer';
import { ErrorProvider } from '@/providers/Error';
import '@/styles/index.scss';
import App from '@/App.tsx';
import { CookieProvider } from './providers/Cookie';
import { initLogger } from './lib/logger';
import { ErrorBoundary } from './components/ErrorBoundary';
import { stripLegacyFlowPrefix } from './routes/legacyFlowPrefix';

// Initialize logger service (and Sentry if configured) BEFORE React renders
initLogger();

// Persist affiliate/CPS landing URL before any routing strips the params
refUrlService.capture();

// Persist the `?flow=` post-signup flow override from the same landing URL
flowOverrideService.capture();

// Before the router reads the URL
stripLegacyFlowPrefix();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      enabled: false,
    },
  },
});

const posthogApiKey = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const posthogAppClientName = import.meta.env.VITE_POSTHOG_APP_CLIENT_NAME;
  const posthogOptions: Partial<PostHogConfig> = {
    api_host: import.meta.env.VITE_POSTHOG_HOST,
    defaults: '2025-05-24',
    opt_out_capturing_by_default: true,
    session_recording: { maskAllInputs: true },
    loaded: (posthog) => {
      posthog.register({ app_client_name: posthogAppClientName });
    },
  };

const renderApp = () => {
  const appTree = (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CookieProvider>
          <SignalRProvider>
            <OfferEventsSubscriber />
            <ErrorProvider>
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </ErrorProvider>
          </SignalRProvider>
        </CookieProvider>
      </AuthProvider>
    </QueryClientProvider>
  );

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      {posthogApiKey ? (
        <PostHogProvider apiKey={posthogApiKey} options={posthogOptions}>
          {appTree}
        </PostHogProvider>
      ) : (
        appTree
      )}
    </StrictMode>
  );
};

const isDevelopment = import.meta.env.MODE === 'development';
const isMockEnabled = import.meta.env.VITE_MOCK_ENABLED === 'true';

if (isDevelopment && isMockEnabled) {
  import('./mocks').then(({ initMocks }) => {
    initMocks().then(() => {
      renderApp();
    });
  });
} else {
  renderApp();
}
